import { ECONOMY } from '@/config/economy';
import { PLAYER } from '@/config/player';
import { lockedCardIds } from '@/core/meta';
import { t, type I18nKey } from '@/i18n';
import { LAYOUTS } from '@/arena/layouts';
import { bossById } from '@/bosses/registry';
import type { CardDef } from '@/upgrades/cards';
import { rollOffer } from '@/upgrades/pool';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import type { ShopState, UpgradeScreen } from '@/ui/upgrade-screen';
import { music } from '@/audio/music';
import { sfx } from '@/audio/sfx';
import type { RunState } from './run-state';
import { layoutIndexFor } from './sector-hooks';
import { bagCap, bagPrice, cardContext, socketPrice } from './ship';
import type { WaveDirector } from './wave-director';

/** Cards the hangar padlocks sell directly (docs/09 wave 4). */
const HANGAR_CARDS = ['rack', 'cargo'];

/** Canvas size for overlay layout (render/stage.ts provides it). */
export interface ScreenSize {
  readonly width: number;
  readonly height: number;
}

/**
 * The UPGRADE BAY between rooms (docs/03, docs/12): offer + reroll, repair
 * and armor buys (the weapon bays are run/hangar.ts). Every transaction
 * autosaves through `save`.
 */
export class ShopFlow {
  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly waves: WaveDirector,
    private readonly screen: UpgradeScreen,
    private readonly hud: Hud,
    private readonly view: ScreenSize,
    private readonly save: () => void,
  ) {}

  /** Shop break: pre-pick + preview the next fight, fresh offer and prices. */
  open(): void {
    const { run, world } = this;
    music.playShop(); // garage anthem while wrenching (user request)
    // Pre-pick the NEXT fight so the shop can preview it (docs/12 B5) —
    // gated by the room it will actually be fought in (X12)
    if (run.nextPick === null) run.nextPick = run.drawFight(run.depth + 1);
    this.rollOffer();
    run.rerollPrice = world.player.stats.rerollLock
      ? ECONOMY.rerollBase
      : ECONOMY.rerollBase + run.roomInSector * ECONOMY.rerollRoomStep;
    run.healPrice = ECONOMY.heal.basePrice;
    run.armorPrice = ECONOMY.armorBuy.basePrice;
    this.screen.show(this.view.width, this.view.height, this.state());
    // Death bay: the tax is still pending — resolveDeath saves once it's paid
    if (!run.deathShop) this.save();
  }

  refresh(): void {
    this.screen.refresh(this.state());
  }

  /** "+N" bloom over the header readout (repair, armor, scrap gems). */
  flashGain(kind: 'armor' | 'hp' | 'gems', amount: number): void {
    this.screen.flashGain(kind, amount);
  }

  state(): ShopState {
    const { run, world } = this;
    const player = world.player;
    // Group the collection by card id for the chip strip
    const grouped = new Map<string, { card: CardDef; count: number }>();
    for (const card of run.ownedCards) {
      const entry = grouped.get(card.id);
      if (entry) entry.count++;
      else grouped.set(card.id, { card, count: 1 });
    }
    const next = run.nextPick ? bossById(run.nextPick.id)!.def : null;
    const nextRoom = run.roomInSector + run.nextRoomStep;
    const layout = LAYOUTS[layoutIndexFor(run, nextRoom, run.arenaIndex + run.nextRoomStep)]!;
    return {
      offer: run.offer,
      yellow: run.gems,
      red: run.red,
      rerollPrice: this.rerollCost,
      healPrice: run.healPrice,
      armorPrice: this.armorCost,
      armorFull: player.armor >= PLAYER.maxArmor,
      arenaName: this.nextRoomLabel(nextRoom, t(layout.nameKey as I18nKey)),
      collection: [...grouped.values()],
      inventory: run.inventory,
      equipped: [...player.weapon.equipped],
      equipCapacity: player.weapon.capacity,
      bagCap: bagCap(player),
      pending: run.pendingWeapon,
      fresh: run.freshWeapons,
      socketPrice: socketPrice(player),
      bagPrice: bagPrice(player, run),
      armorItems: run.storedArmor,
      next: next && run.nextPick
        ? {
            name: t(next.nameKey as I18nKey),
            bodyKey: next.bodyKey,
            level: run.nextPick.level,
            waves: this.waves.previewWaves(),
          }
        : null,
      ctx: cardContext(player, run),
    };
  }

  /** Sector runs tag the next arena with its room (or FINALE) — docs/01. */
  private nextRoomLabel(room: number, arena: string): string {
    const sector = this.run.sector;
    if (!sector) return arena;
    if (room >= sector.rooms) return t('shop.sectorFinale', { arena });
    return t('shop.sectorRoom', { room: room + 1, total: sector.rooms + 1, arena });
  }

  /** Shop input: focus moves + confirm / scrap / info / reroll shortcuts. */
  handleInput(
    confirm: boolean, reroll: boolean, trash: boolean, info: boolean,
    moveXEdge: number, moveYEdge: number,
  ): void {
    const screen = this.screen;
    if (moveXEdge !== 0) screen.moveSelection(moveXEdge);
    if (moveYEdge !== 0) screen.moveVertical(moveYEdge);
    if (confirm) screen.activateSelected();
    else if (trash) screen.trashSelected();
    else if (info) screen.toggleInfo();
    else if (reroll) this.reroll();
  }

  /** New offer, carrying locked (and unbought) cards over untouched. */
  private rollOffer(): void {
    const run = this.run;
    const kept = run.offer.filter((o) => o.locked && !o.bought);
    // Achievement-locked cards stay out of the pool until earned (docs/01);
    // sockets + bag slots are sold on their hangar padlocks, not as cards
    const exclude = [...kept.map((k) => k.card.id), ...lockedCardIds(), ...HANGAR_CARDS];
    run.offer = [
      ...kept,
      ...rollOffer(
        run.rng, cardContext(this.world.player, run), run.roomInSector,
        Math.max(0, ECONOMY.offerSize - kept.length), exclude,
      ),
    ];
  }

  /** Toggle the keep-lock on an offer card (X / Ⓧ or the padlock button). */
  toggleLock(index: number): void {
    const oc = this.run.offer[index];
    if (!oc || oc.bought) {
      sfx.uiDeny();
      return;
    }
    oc.locked = !oc.locked;
    sfx.uiConfirm();
    this.refresh();
  }

  buyCard(index: number): void {
    const run = this.run;
    const oc = run.offer[index];
    if (!oc || oc.bought) return;
    const wallet = oc.currency === 'red' ? run.red : run.gems;
    if (wallet < oc.price) return;
    if (oc.currency === 'red') run.red -= oc.price;
    else run.gems -= oc.price;
    oc.bought = true;
    oc.card.apply(cardContext(this.world.player, run));
    run.ownedCards.push(oc.card);
    sfx.uiConfirm();
    this.refresh();
    this.save();
  }

  /** Escalating base price after the Reroll Voucher cut (docs/12 I6). */
  private get rerollCost(): number {
    const steps = ECONOMY.rerollDiscountByStack;
    const mul = steps[Math.min(this.world.player.stats.rerollDiscountSteps, steps.length - 1)]!;
    return Math.max(ECONOMY.rerollMin, Math.round(this.run.rerollPrice * mul));
  }

  reroll(): void {
    const run = this.run;
    if (run.gems < this.rerollCost) return;
    run.gems -= this.rerollCost;
    // Lucky Chip card freezes the escalation
    if (!this.world.player.stats.rerollLock) run.rerollPrice += ECONOMY.rerollStep;
    this.rollOffer();
    this.refresh();
    this.save();
  }

  /** +25 HP for red cores; the price ladder (2→4→6…) resets each break. */
  buyHeal(): void {
    const { run, world } = this;
    const player = world.player;
    if (run.red < run.healPrice || player.hp >= player.maxHp) {
      sfx.uiDeny();
      return;
    }
    run.red -= run.healPrice;
    run.healPrice += ECONOMY.heal.priceStep;
    player.hp = Math.min(player.maxHp, player.hp + PLAYER.cellHp);
    sfx.uiConfirm();
    this.refresh();
    this.screen.flashGain('hp', PLAYER.cellHp); // gain animation (docs/12 #1)
    this.save();
  }

  /** Armor price after the Plating Deal card cut (docs/12 O). */
  private get armorCost(): number {
    return Math.max(
      ECONOMY.armorBuy.minPrice,
      Math.round(this.run.armorPrice * this.world.player.stats.armorPriceMul),
    );
  }

  /** +1 armor unit for YELLOW gems — pricey, climbs per buy (docs/12 O). */
  buyArmor(): void {
    const { run, world } = this;
    if (run.gems < this.armorCost || world.player.armor >= PLAYER.maxArmor) {
      sfx.uiDeny();
      return;
    }
    run.gems -= this.armorCost;
    run.armorPrice += ECONOMY.armorBuy.priceStep;
    this.gainArmor();
    this.screen.flashGain('armor', 1); // gain animation (docs/12 #1)
    this.save();
  }

  /** Hangar armor stack → one yellow armor unit on the ship (docs/12 H3). */
  useStoredArmor(): void {
    const { run, world } = this;
    if (run.storedArmor <= 0 || world.player.armor >= PLAYER.maxArmor) {
      sfx.uiDeny();
      return;
    }
    run.storedArmor--;
    this.gainArmor();
    this.save();
  }

  private gainArmor(): void {
    this.world.player.armor++;
    this.hud.flashArmor();
    sfx.uiConfirm();
    this.refresh();
  }
}
